import { Badge } from '@systemeio/components'
import React, { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { FormControlSizeProvider } from 'shared/components/form-control-size-context'
import { Tooltip } from 'shared/components/tooltip'
import { twMerge } from 'tailwind-merge'

/* Width from which the card is the medium one; the `@min-[500px]` / `@max-[500px]` classes repeat it. */
const MEDIUM_CARD_MIN_WIDTH = 500

interface ChartCardProps {
  children: ReactNode
  className?: string
  headerAction?: ReactNode
  id?: string
  title: ReactNode
  value?: ReactNode
  valueAction?: ReactNode
  valueLabel?: ReactNode
}

/*
 * The card takes its size from its own width, not from the viewport. The root is the size
 * container, so the padding sits on an inner wrapper: a container query cannot style the container
 * itself. The wrapper fills the root, so a card given a fixed height hands what the header leaves to
 * the chart.
 */
const ChartCardRoot = ({
  children,
  className,
  id,
}: {
  children: ReactNode
  className?: string
  id?: string
}) => (
  <div
    id={id}
    data-chart-card={''}
    className={twMerge(
      'border-border-100 bg-bg-primary @container flex w-full flex-col rounded-lg border',
      className,
    )}
  >
    <div className={'flex min-h-0 flex-1 flex-col gap-4 p-4 @min-[500px]:px-6 @min-[500px]:py-5'}>
      {children}
    </div>
  </div>
)

/*
 * One line at every size: the title truncates first, while the badges and controls on either side
 * keep their size and stay at least 16px away from it. The one exception is a control on the small
 * card, which takes a row of its own under the title.
 */
const ChartCardHeader = ({ children }: { children: ReactNode }) => (
  <div
    className={
      'flex items-center justify-between gap-x-4 gap-y-3 @max-[500px]:has-[[data-chart-card-control]]:flex-wrap'
    }
  >
    {children}
  </div>
)

/* A plain-text title truncates and shows in full on hover; a composed one handles its overflow. */
const ChartCardTitle = ({ children, action }: { children: ReactNode; action?: ReactNode }) => (
  <div
    className={
      'text-text-primary flex min-w-0 items-center gap-2 text-base font-medium @min-[500px]:text-lg'
    }
  >
    {typeof children === 'string' ? (
      <Tooltip mode={'overflow'} label={children}>
        {children}
      </Tooltip>
    ) : (
      children
    )}
    {action !== undefined && <div className={'shrink-0'}>{action}</div>}
  </div>
)

const ChartCardValue = ({ children, label }: { children: ReactNode; label?: ReactNode }) => (
  <Badge
    className={'shrink-0 text-base'}
    color={'outlinedGray'}
    render={<div />}
    shape={'rounded'}
    size={'lg'}
  >
    {label !== undefined && (
      <span className={'text-text-secondary @max-[500px]:hidden'}>{label}</span>
    )}
    <div className={'font-medium'}>{children}</div>
  </Badge>
)

/*
 * A control in place of the value, such as a select between series. On the small card it spans
 * the card under the title and is the 40px field, on the medium one it sits at the end of the
 * header as the 32px field: controls inside read the size from `FormControlSizeProvider`. The
 * field size is a prop, not a style, so the card width is measured here rather than queried in CSS.
 */
const ChartCardControl = ({ children }: { children: ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null)
  const [isSmallCard, setIsSmallCard] = useState(false)

  useLayoutEffect(() => {
    const card = ref.current?.closest<HTMLElement>('[data-chart-card]')

    if (!card) return

    /*
     * The content box, as the container query measures it: the border box would keep the 32px field
     * for 2px after the `@max-[500px]` classes have already moved the control to its own row. The
     * root has no padding, so `clientWidth` is that width.
     */
    const update = (width: number) => setIsSmallCard(width < MEDIUM_CARD_MIN_WIDTH)

    update(card.clientWidth)

    const observer = new ResizeObserver(([entry]) => {
      if (entry) update(entry.contentRect.width)
    })
    observer.observe(card)

    return () => observer.disconnect()
  }, [])

  return (
    <div ref={ref} data-chart-card-control={''} className={'shrink-0 @max-[500px]:basis-full'}>
      <FormControlSizeProvider size={isSmallCard ? 'md' : 'sm'}>{children}</FormControlSizeProvider>
    </div>
  )
}

/* Stays `relative` because `chart-config.ts` positions its DOM tooltip inside it. */
const ChartCardBody = ({ children }: { children: ReactNode }) => (
  <div className={'relative min-h-0 flex-1'}>{children}</div>
)

const ChartCardPreset = ({
  children,
  className,
  headerAction,
  id,
  title,
  value,
  valueAction,
  valueLabel,
}: ChartCardProps) => (
  <ChartCardRoot id={id} className={className}>
    <ChartCardHeader>
      <ChartCardTitle action={headerAction}>{title}</ChartCardTitle>
      {(valueAction !== undefined || value !== undefined) && (
        <div className={'flex shrink-0 items-center gap-3'}>
          {valueAction}
          {value !== undefined && <ChartCardValue label={valueLabel}>{value}</ChartCardValue>}
        </div>
      )}
    </ChartCardHeader>
    <ChartCardBody>{children}</ChartCardBody>
  </ChartCardRoot>
)

const ChartCard = Object.assign(ChartCardPreset, {
  Root: ChartCardRoot,
  Header: ChartCardHeader,
  Title: ChartCardTitle,
  Value: ChartCardValue,
  Control: ChartCardControl,
  Body: ChartCardBody,
})

export { ChartCard }
export type { ChartCardProps }
